import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { usePonds, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface ExpenseFormData {
  expense_date: string;
  amount: number;
  expense_type: string;
  pond_id?: string;
  shed_id?: string; // For goat farming
  business_type?: string; // Auto-populated
  description: string;
  receipt_number?: string;
  vendor?: string;
}

const expenseSchema = yup.object({
  expense_date: yup.string().required('Expense date is required'),
  amount: yup.number().positive('Amount must be positive').required('Amount is required'),
  expense_type: yup.string().required('Please select expense type'),
  pond_id: yup.string().optional(),
  description: yup.string().required('Description is required'),
  receipt_number: yup.string().optional(),
  vendor: yup.string().optional(),
});

interface ExpenseLog {
  id: string;
  expense_date: string;
  amount: number;
  expense_type: string;
  pond_id?: string;
  description: string;
  receipt_number?: string;
  vendor?: string;
  created_at: string;
  created_by: string;
}

const ExpenseForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const { businessType } = useBusinessTypeContext();
  const [expenseLogs, setExpenseLogs] = useState<ExpenseLog[]>([]);
  const [yearSummary, setYearSummary] = useState<{ year: number; total: number; by_type: Record<string, number> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sheds, setSheds] = useState<any[]>([]);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { mutate: createExpenseLog, loading: creating } = useMutation<ExpenseLog, ExpenseFormData>();

  // Load goat sheds if business type is goat
  useEffect(() => {
    if (businessType === 'goat' && selectedLocation?.id) {
      loadGoatSheds();
    }
  }, [businessType, selectedLocation?.id]);

  const loadGoatSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err) {
      console.error('Failed to load goat sheds:', err);
    }
  };

  // Check if user can delete expenses (admin only)
  const canDelete = user?.role === 'admin';

  const form = useForm<ExpenseFormData>({
    resolver: yupResolver(expenseSchema) as any,
    defaultValues: {
      expense_date: new Date().toISOString().split('T')[0],
      amount: 0,
      expense_type: '',
      pond_id: '',
      description: '',
      receipt_number: '',
      vendor: '',
    }
  });

  // All expense types for display purposes (including feed/medicine for existing records)
  const allExpenseTypes = [
    { value: 'feed', label: 'Feed', color: 'bg-green-100 text-green-800' },
    { value: 'medicine', label: 'Medicine', color: 'bg-red-100 text-red-800' },
    { value: 'salary', label: 'Salary', color: 'bg-blue-100 text-blue-800' },
    { value: 'maintenance', label: 'Maintenance', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'diesel', label: 'Diesel/Fuel', color: 'bg-gray-100 text-gray-800' },
    { value: 'construction', label: 'Construction', color: 'bg-orange-100 text-orange-800' },
    { value: 'goat_purchase', label: 'Goat purchase', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'misc', label: 'Miscellaneous', color: 'bg-purple-100 text-purple-800' },
  ];

  // Expense types available for manual entry (feed/medicine removed)
  // For goat: show construction and goat_purchase, hide diesel
  // For fish: show diesel, hide construction and goat_purchase
  const expenseTypes = allExpenseTypes.filter(type => {
    if (type.value === 'feed' || type.value === 'medicine') return false;
    if (businessType === 'goat' && type.value === 'diesel') return false;
    if (businessType === 'fish' && (type.value === 'construction' || type.value === 'goat_purchase')) return false;
    return true;
  });

  useEffect(() => {
    if (selectedLocation?.id) {
      loadExpenseLogs();
    }
  }, [selectedLocation?.id]);

  const loadExpenseLogs = async () => {
    try {
      setLoading(true);
      // Use goat-specific endpoint if business type is goat
      const endpoint = businessType === 'goat' 
        ? `/goat/expense-logs?location_id=${selectedLocation?.id}`
        : `/locations/${selectedLocation?.id}/expense-logs`;
      const response = await apiClient.get(endpoint);
      setExpenseLogs(response.data.logs ?? response.data);
      if (response.data.current_year_summary) {
        setYearSummary(response.data.current_year_summary);
      } else {
        setYearSummary(null);
      }
    } catch (err: any) {
      setError('Failed to load expense logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: ExpenseFormData) => {
    try {
      const expenseData = {
        ...data,
        location_id: selectedLocation?.id,
        // Clear pond_id if goat, clear shed_id if fish
        pond_id: businessType === 'fish' ? data.pond_id : undefined,
        shed_id: businessType === 'goat' ? data.shed_id : undefined,
      };
      // Use goat-specific endpoint if business type is goat
      const endpoint = businessType === 'goat' ? '/goat/expense-logs' : '/expense-logs';
      const newLog = await createExpenseLog(endpoint, expenseData);
      if (newLog) {
        setExpenseLogs([newLog, ...expenseLogs]);
        form.reset({
          expense_date: new Date().toISOString().split('T')[0],
          amount: 0,
          expense_type: '',
          pond_id: '',
          description: '',
          receipt_number: '',
          vendor: '',
        });
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (expenseId: string, amount: number, description: string) => {
    if (!canDelete) {
      setError('You do not have permission to delete expenses');
      return;
    }

    const confirmMessage = `Are you sure you want to delete this expense?\n\nAmount: ₹${amount.toLocaleString()}\nDescription: ${description}\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingId(expenseId);
      setError('');
      // Use goat-specific endpoint if business type is goat
      const endpoint = businessType === 'goat' ? `/goat/expense-logs/${expenseId}` : `/expense-logs/${expenseId}`;
      await apiClient.delete(endpoint);
      // Remove the deleted expense from the list
      setExpenseLogs(expenseLogs.filter(log => log.id !== expenseId));
      // Reload to refresh the summary
      await loadExpenseLogs();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete expense');
    } finally {
      setDeletingId(null);
    }
  };

  const getPondName = (pondId: string) => {
    const pond = ponds?.find(p => p.id === pondId);
    return pond ? `${pond.name} (${pond.pond_type})` : 'General';
  };

  const getExpenseTypeColor = (type: string) => {
    const expenseType = allExpenseTypes.find(et => et.value === type);
    return expenseType?.color || 'bg-gray-100 text-gray-800';
  };

  const getExpenseTypeLabel = (type: string) => {
    const expenseType = allExpenseTypes.find(et => et.value === type);
    return expenseType?.label || type;
  };

  const getTotalExpenses = () => {
    return expenseLogs.reduce((total, log) => total + log.amount, 0);
  };

  // Parse date string to Date object, handling multiple formats
  // Prioritizes DD/MM/YYYY format (common in India)
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Remove time part if present
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    
    // Check if it's already in ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return new Date(dateOnly + 'T00:00:00');
    }
    
    // Try DD/MM/YYYY format (common in India)
    const slashMatch = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, part1, part2, year] = slashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      // If first part > 12, it must be DD/MM/YYYY
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1);
      }
      // If second part > 12, it must be MM/DD/YYYY
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2);
      }
      // Ambiguous case: try DD/MM/YYYY first (more common in India)
      // Check if the date makes sense as DD/MM
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM; // DD/MM/YYYY
      }
      // Otherwise try MM/DD/YYYY
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Try other formats with dashes
    const dashMatch = dateOnly.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashMatch) {
      const [, part1, part2, year] = dashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1); // DD-MM-YYYY
      }
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2); // MM-DD-YYYY
      }
      // Try DD-MM-YYYY first
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM;
      }
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Fallback to Date constructor
    const parsed = new Date(dateOnly);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Normalize date to ISO format (YYYY-MM-DD) for grouping
  const normalizeDateToISO = (dateStr: string): string => {
    if (!dateStr) return '';
    
    const date = parseDateString(dateStr);
    if (!date) {
      console.warn('Failed to parse date:', dateStr);
      // If it's already in ISO format, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      return dateStr; // Return original if parsing fails
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateForDisplay = (dateStr: string): string => {
    const date = parseDateString(dateStr);
    if (!date) return dateStr;
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Group expense logs by date and calculate daily summaries
  const getGroupedLogsByDate = () => {
    const grouped: { [date: string]: { logs: ExpenseLog[]; totalAmount: number; displayDate: string } } = {};
    
    expenseLogs.slice(0, 100).forEach((log) => {
      // Normalize date to ISO format for grouping - use expense_date, not created_at
      const normalizedDate = normalizeDateToISO(log.expense_date);
      
      if (!normalizedDate) {
        // Skip logs with invalid dates
        console.warn('Skipping log with invalid expense_date:', log.expense_date, log.id);
        return;
      }
      
      if (!grouped[normalizedDate]) {
        // Use the first log's expense_date for display date
        grouped[normalizedDate] = { 
          logs: [], 
          totalAmount: 0, 
          displayDate: formatDateForDisplay(log.expense_date) 
        };
      }
      
      grouped[normalizedDate].logs.push(log);
      grouped[normalizedDate].totalAmount += log.amount || 0;
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(grouped)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/data-entry" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💰 Expense Log</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Card */}
      {expenseLogs.length > 0 && (
        <div className="card p-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Expenses Logged</span>
            <span className="text-lg font-bold text-red-600">
              ₹{((yearSummary?.total ?? getTotalExpenses())).toLocaleString()}
            </span>
          </div>
          {yearSummary && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(yearSummary.by_type).map(([type, amount]) => (
                <span key={type} className={`px-2 py-1 text-xs rounded-full ${getExpenseTypeColor(type)}`}>
                  {getExpenseTypeLabel(type)}: ₹{Math.round(amount).toLocaleString()}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expense Entry Form */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Record Expense
        </h2>

        {/* Informational Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800 mb-2">
            <strong>📦 Inventory Items:</strong> Feed and Medicine purchases should be recorded in{' '}
            <Link to="/admin/inventory/purchases" className="underline font-semibold">
              Inventory Purchase
            </Link>
            . Expenses are automatically created when these items are consumed.
          </p>
          <p className="text-xs text-blue-700 mt-1">
            • Feed expenses: Created automatically when feed is used in ponds<br/>
            • Medicine expenses: Created automatically when medicine is applied to ponds
          </p>
        </div>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Date *</label>
              <input
                type="date"
                {...form.register('expense_date')}
                className="form-input"
              />
              {form.formState.errors.expense_date && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.expense_date.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Amount (₹) *</label>
              <input
                type="number"
                step="0.01"
                {...form.register('amount')}
                className="form-input"
                placeholder="0.00"
              />
              {form.formState.errors.amount && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Expense Type *</label>
              <select {...form.register('expense_type')} className="form-input">
                <option value="">Select Type</option>
                {expenseTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {form.formState.errors.expense_type && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.expense_type.message}</p>
              )}
            </div>

            {businessType === 'fish' ? (
              <div>
                <label className="form-label">Associated Pond</label>
                <select {...form.register('pond_id')} className="form-input">
                  <option value="">General (Not pond-specific)</option>
                  {ponds?.map((pond) => (
                    <option key={pond.id} value={pond.id}>
                      {pond.name} - {pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="form-label">Associated Shed</label>
                <select {...form.register('shed_id')} className="form-input">
                  <option value="">General (Not shed-specific)</option>
                  {sheds.map((shed) => (
                    <option key={shed.id} value={shed.id}>
                      {shed.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="form-label">Description *</label>
              <textarea
                {...form.register('description')}
                className="form-input"
                rows={2}
                placeholder="What was purchased or paid for?"
              />
              {form.formState.errors.description && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.description.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Receipt Number</label>
              <input
                type="text"
                {...form.register('receipt_number')}
                className="form-input"
                placeholder="Receipt/Bill number"
              />
            </div>

            <div>
              <label className="form-label">Vendor/Supplier</label>
              <input
                type="text"
                {...form.register('vendor')}
                className="form-input"
                placeholder="Vendor or supplier name"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full btn-touch bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center"
          >
            {creating ? <LoadingSpinner size="sm" /> : 'Record Expense'}
          </button>
        </form>
      </div>

      {/* Recent Expense Logs */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Expenses</h2>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : expenseLogs.length > 0 ? (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {getGroupedLogsByDate().map((dayGroup) => (
              <div key={dayGroup.date} className="space-y-2">
                {/* Daily Summary Strip */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-3 sticky top-0 z-10">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <span className="font-semibold text-gray-700">
                        {dayGroup.displayDate}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Total Expenses</div>
                        <div className="font-bold text-red-700">₹{dayGroup.totalAmount.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Individual Expenses for this day */}
                {dayGroup.logs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded-lg p-4 ml-2">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-bold text-red-600">₹{log.amount.toLocaleString()}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getExpenseTypeColor(log.expense_type)}`}>
                            {getExpenseTypeLabel(log.expense_type)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="font-medium">{log.description}</div>
                          {log.pond_id && <div>Pond: {getPondName(log.pond_id)}</div>}
                          {log.vendor && <div>Vendor: {log.vendor}</div>}
                          {log.receipt_number && <div>Receipt: {log.receipt_number}</div>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="text-xs text-gray-500">
                          {formatDateForDisplay(log.expense_date)}
                        </div>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(log.id, log.amount, log.description)}
                            disabled={deletingId === log.id}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete expense"
                          >
                            {deletingId === log.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <TrashIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">💰</div>
            <p className="text-gray-500">No expense records yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseForm;